/**
 * Per-kind inline cell editors for board rows.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import type { BoardColumn, StatusOption, DropdownOption } from './types';
import { useUsersForBoards, type BoardUser } from './api';
import { SheetEditor } from './SheetEditor';

// ---------- anchored menu (portaled to <body>, fixed position) ----------
// The table scroll container (overflow-x:auto) clips absolutely-positioned
// menus, so cell dropdowns are rendered into document.body and positioned
// against the trigger's viewport rect — never cut off by the table edge.
interface MenuCoords { top: number; left: number; width: number; flip: boolean }
function useAnchoredMenu(estHeight = 280) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 220);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 12));
    const below = window.innerHeight - r.bottom;
    const flip = below < estHeight && r.top > below; // open upward if not enough room below
    const top = flip ? Math.max(8, r.top - 6) : r.bottom + 5;
    setCoords({ top, left, width, flip });
  };
  const openMenu = () => { place(); setOpen(true); };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return { open, setOpen, coords, triggerRef, menuRef, openMenu };
}

const portalStyle = (c: MenuCoords): React.CSSProperties => ({
  position: 'fixed',
  left: c.left,
  minWidth: c.width,
  zIndex: 320,
  ...(c.flip ? { bottom: window.innerHeight - c.top } : { top: c.top }),
});

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  gray:   { bg: '#e5e7eb', fg: '#374151' },
  green:  { bg: '#d1fae5', fg: '#065f46' },
  yellow: { bg: '#fef3c7', fg: '#92400e' },
  red:    { bg: '#fee2e2', fg: '#991b1b' },
  blue:   { bg: '#dbeafe', fg: '#1e40af' },
  purple: { bg: '#e9d5ff', fg: '#6b21a8' },
  pink:   { bg: '#fce7f3', fg: '#9d174d' },
  orange: { bg: '#fed7aa', fg: '#9a3412' },
};

interface CellProps {
  column: BoardColumn;
  value: unknown;
  onChange: (v: unknown) => void;
}

// ---------- TEXT ----------
const TextCell: React.FC<CellProps> = ({ value, onChange }) => {
  const [local, setLocal] = useState((value as string) ?? '');
  useEffect(() => { setLocal((value as string) ?? ''); }, [value]);
  return (
    <input
      className="bd-cell-input"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value ?? '')) onChange(local); }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      placeholder="—"
    />
  );
};

// ---------- NUMBER (plain · currency · progress bar) ----------
const progressHue = (v: number) => (v >= 100 ? 158 : v >= 50 ? 274 : 55);

const NumberCell: React.FC<CellProps> = ({ column, value, onChange }) => {
  const cfg = (column.config || {}) as { prefix?: string; suffix?: string; display?: string };
  const isProgress = cfg.display === 'progress' || cfg.suffix === '%';
  const isCurrency = !isProgress && (cfg.display === 'currency' || !!cfg.prefix || !!cfg.suffix);
  const isFormatted = isProgress || isCurrency;
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value == null ? '' : String(value));
  useEffect(() => { setLocal(value == null ? '' : String(value)); }, [value]);

  const commit = () => {
    setEditing(false);
    if (local === '') { if (value != null) onChange(null); return; }
    const n = Number(local);
    if (!Number.isNaN(n) && n !== value) onChange(n);
  };

  // Always-on input for plain numbers; click-to-edit for currency/progress.
  if (!isFormatted || editing) {
    return (
      <input
        type="number"
        className="bd-cell-input bd-cell-input--num"
        value={local}
        autoFocus={editing}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); else if (e.key === 'Escape') { setLocal(value == null ? '' : String(value)); setEditing(false); } }}
        placeholder="—"
      />
    );
  }

  const num = value == null ? null : Number(value);

  if (isProgress) {
    const v = Math.max(0, Math.min(100, num ?? 0));
    return (
      <div className="bd-prog" onClick={() => setEditing(true)} title="Cliquer pour modifier">
        <div className="bd-prog-track"><div className="bd-prog-fill" style={{ width: `${v}%`, background: `oklch(0.6 0.16 ${progressHue(v)})` }} /></div>
        <span className="bd-prog-val">{v}%</span>
      </div>
    );
  }

  // currency: honour explicit prefix/suffix, else fall back to a "€" suffix.
  const prefix = cfg.prefix;
  const suffix = cfg.suffix || (cfg.display === 'currency' && !cfg.prefix ? '€' : '');
  const isEuro = suffix === '€' || prefix === '€';
  const display = num == null
    ? null
    : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: isEuro ? 0 : 2 }).format(num);
  return (
    <button type="button" className="bd-money" onClick={() => setEditing(true)}>
      {display == null
        ? <span className="bd-cell-placeholder">—</span>
        : <>{prefix && <small>{prefix} </small>}{display}{suffix && <small> {suffix}</small>}</>}
    </button>
  );
};

// ---------- DATE ----------
const DateCell: React.FC<CellProps> = ({ value, onChange }) => {
  const v = (value as string) ?? '';
  return (
    <input
      type="date"
      className="bd-cell-input"
      value={v}
      onChange={e => onChange(e.target.value || null)}
    />
  );
};

// ---------- CHECKBOX ----------
const CheckboxCell: React.FC<CellProps> = ({ value, onChange }) => (
  <input
    type="checkbox"
    className="bd-cell-checkbox"
    checked={!!value}
    onChange={e => onChange(e.target.checked)}
  />
);

// ---------- STATUS ----------
const StatusCell: React.FC<CellProps> = ({ column, value, onChange }) => {
  const { open, setOpen, coords, triggerRef, menuRef, openMenu } = useAnchoredMenu();
  const options = (column.config?.options || []) as StatusOption[];
  const current = options.find(o => o.id === value);
  const palette = current ? STATUS_COLORS[current.color] || STATUS_COLORS.gray : null;
  return (
    <div className="bd-cell-status">
      <button
        ref={triggerRef}
        type="button"
        className="bd-status-trigger"
        style={palette ? { background: palette.bg, color: palette.fg } : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        {current ? current.label : <span className="bd-cell-placeholder">—</span>}
      </button>
      {open && coords && createPortal(
        <div ref={menuRef} className="bd-status-menu bd-menu-portal" style={portalStyle(coords)}>
          {options.length === 0 && (
            <div className="bd-status-empty">Aucune option (édite la colonne)</div>
          )}
          {options.map(opt => {
            const p = STATUS_COLORS[opt.color] || STATUS_COLORS.gray;
            return (
              <button
                key={opt.id}
                type="button"
                className="bd-status-option"
                style={{ background: p.bg, color: p.fg }}
                onClick={() => { onChange(opt.id); setOpen(false); }}
              >
                {opt.label}
              </button>
            );
          })}
          {!!current && (
            <button
              type="button"
              className="bd-status-clear"
              onClick={() => { onChange(null); setOpen(false); }}
            >
              Effacer
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

// ---------- DROPDOWN ----------
const DropdownCell: React.FC<CellProps> = ({ column, value, onChange }) => {
  const options = (column.config?.options || []) as DropdownOption[];
  return (
    <select
      className="bd-cell-input bd-cell-select"
      value={(value as string) ?? ''}
      onChange={e => onChange(e.target.value || null)}
    >
      <option value="">—</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
};

// ---------- PERSON (autocomplete on real users; stores user id) ----------
const userLabel = (u: BoardUser) =>
  `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;

const userInitials = (u: BoardUser) => {
  const a = (u.first_name || u.email || '?').charAt(0);
  const b = (u.last_name || '').charAt(0);
  return (a + b).toUpperCase();
};

const userColor = (id: string) => {
  // Deterministic pastel from user id
  const palette = ['#fef3c7', '#d1fae5', '#dbeafe', '#fce7f3', '#fed7aa', '#e9d5ff', '#fee2e2'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const PersonCell: React.FC<CellProps> = ({ value, onChange }) => {
  const { data: users = [] } = useUsersForBoards();
  const { open, setOpen, coords, triggerRef, menuRef, openMenu } = useAnchoredMenu(320);
  const [query, setQuery] = useState('');

  const current = useMemo(() => users.find(u => u.id === value), [users, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      userLabel(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, query]);

  return (
    <div className="bd-cell-person">
      <button
        ref={triggerRef}
        type="button"
        className="bd-person-trigger"
        onClick={() => { if (open) { setOpen(false); } else { setQuery(''); openMenu(); } }}
      >
        {current ? (
          <>
            <span className="bd-person-avatar" style={{ background: userColor(current.id) }}>
              {userInitials(current)}
            </span>
            <span className="bd-person-name">{userLabel(current)}</span>
          </>
        ) : (
          <span className="bd-cell-placeholder">— Assigner</span>
        )}
      </button>
      {open && coords && createPortal(
        <div ref={menuRef} className="bd-person-menu bd-menu-portal" style={portalStyle(coords)}>
          <input
            className="bd-person-search"
            autoFocus
            placeholder="Chercher…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {filtered.length === 0 && (
            <div className="bd-person-empty">Aucun utilisateur</div>
          )}
          {filtered.map(u => (
            <button
              key={u.id}
              type="button"
              className="bd-person-option"
              onClick={() => { onChange(u.id); setOpen(false); }}
            >
              <span className="bd-person-avatar" style={{ background: userColor(u.id) }}>
                {userInitials(u)}
              </span>
              <span className="bd-person-name">{userLabel(u)}</span>
              <span className="bd-person-email">{u.email}</span>
            </button>
          ))}
          {!!current && (
            <button
              type="button"
              className="bd-person-clear"
              onClick={() => { onChange(null); setOpen(false); }}
            >
              Désassigner
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

// ---------- SHEET (rich-text mini document, stored as HTML on a text column) ----------
const SheetCell: React.FC<CellProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const html = (value as string) || '';
  const preview = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  return (
    <>
      <button type="button" className="bd-sheet-cell" onClick={() => setOpen(true)} title="Ouvrir la feuille">
        <DocumentTextIcon className="w-4 h-4" />
        <span className="bd-sheet-cell-text">{preview || 'Ouvrir la feuille…'}</span>
      </button>
      {open && (
        <SheetEditor
          initialHtml={html}
          onSave={h => { onChange(h); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const Cell: React.FC<CellProps> = (p) => {
  if (p.column.kind === 'text' && p.column.config?.variant === 'sheet') return <SheetCell {...p} />;
  switch (p.column.kind) {
    case 'text':     return <TextCell     {...p} />;
    case 'number':   return <NumberCell   {...p} />;
    case 'date':     return <DateCell     {...p} />;
    case 'checkbox': return <CheckboxCell {...p} />;
    case 'status':   return <StatusCell   {...p} />;
    case 'dropdown': return <DropdownCell {...p} />;
    case 'person':   return <PersonCell   {...p} />;
    default:         return <TextCell     {...p} />;
  }
};

export { STATUS_COLORS };
